import { useInventoryDrones } from '@/lib/api/queries';
import { AircraftPanel } from './AircraftPanel';
import { FlightDetail } from './FlightDetail';
import { useFlightsStore } from './FlightsStore';
import { FlightsTable, ServerFilterControls, SourceFilterChips } from './FlightsTable';

/**
 * Flights context — a full-width overlay that replaces the map area.
 *
 * The mockup hides `.map-area`, `.lpanel` and `#fs-btn` for this context
 * (`body.flights-ctx`, mock CSS L1647-1652); in v2 that is the existing
 * overlay-context pattern shared with Inventory / RTM / Apps, handled by
 * `BodyGrid`'s `isOverlayCtx` branch rather than by CSS.
 */
export function FlightsOverlay() {
  const openFlightId = useFlightsStore((s) => s.openFlightId);

  return (
    <div className="absolute inset-0 bg-(--color-bg) overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-3 p-3">
      <section className="rounded-md border border-(--color-border) bg-(--color-surface) overflow-hidden lg:col-span-3 min-h-[280px]">
        <AircraftPanel />
      </section>
      <section className="rounded-md border border-(--color-border) bg-(--color-surface) overflow-hidden lg:col-span-9 min-h-[280px] flex flex-col">
        {openFlightId ? <FlightDetail /> : <FlightsListPane />}
      </section>
    </div>
  );
}

function FlightsListPane() {
  const serial = useFlightsStore((s) => s.selectedSerial);
  const { data: drones } = useInventoryDrones();
  const drone = (drones ?? []).find((d) => d.serial_number === serial);
  const label = drone ? (drone.ec_callsign ?? drone.display_name) : null;

  return (
    <>
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0 flex items-center gap-2">
        <span className="text-[0.923em] font-bold truncate">
          {label ? `Flights — ${label}` : 'Flights'}
        </span>
        {/* Server-side filters lead, client-side chips follow — the order
            they apply in. `ServerFilterControls` renders nothing in demo
            mode, where MSW ignores query params. */}
        <div className="ml-auto shrink-0 flex items-center gap-2 flex-wrap justify-end">
          <ServerFilterControls />
          <SourceFilterChips />
        </div>
      </header>
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <FlightsTable />
      </div>
    </>
  );
}
